import styled from 'styled-components';
import { FlexColumn } from '@elementary/common-ui/components';
import { ColorToken } from '@elementary/common-ui/style/types';
import { getColor } from '@elementary/common-ui/style/utils';
import { Box } from '@mui/material';

const INSET_SHADOW_OFFSET = '0.75px 1.5px 4.425px 0px';

const getCoverageProperties = (
  value: number,
): { color: ColorToken; barCount: number } => {
  if (value === 0) {
    return {
      color: 'status.error.main',
      barCount: 0,
    };
  }
  if (value <= 25) {
    return {
      color: 'status.error.main',
      barCount: 1,
    };
  }
  if (value <= 50) {
    return {
      color: 'status.warning.accent',
      barCount: 2,
    };
  }
  if (value <= 75) {
    return {
      color: 'chart.highlight',
      barCount: 3,
    };
  }
  return {
    color: 'status.success.main',
    barCount: 4,
  };
};

const Bar = styled(Box)<{
  $color?: ColorToken;
  $size: 'small' | 'large';
  $active?: boolean;
}>`
  width: ${({ $size }) => ($size === 'small' ? 14 : 20)}px;
  height: ${({ $size }) => ($size === 'small' ? 2.5 : 3.5)}px;
  border-radius: ${({ $size }) => ($size === 'small' ? 1 : 1.5)}px;
  background: ${({ theme, $color }) =>
    $color ? getColor(theme, $color) : theme.palette.surface.raised};
  box-shadow: ${({ $active, theme }) =>
    $active
      ? 'none'
      : `${INSET_SHADOW_OFFSET} inset ${theme.palette.shadow.solidMuted}`};
`;

const BarsWrapper = styled(FlexColumn)<{ $size: 'small' | 'large' }>`
  align-items: center;
  justify-content: center;
  gap: ${({ $size }) => ($size === 'small' ? 1.5 : 2.5)}px;
`;

const BarStack = ({
  value,
  size = 'small',
}: {
  value: number;
  size?: 'small' | 'large';
}) => {
  const { color, barCount } = getCoverageProperties(value);
  const emptyBars = 4 - barCount;

  return (
    <BarsWrapper $size={size}>
      {Array(emptyBars)
        .fill(null)
        .map((_, i) => (
          <Bar key={`empty-${i}`} $size={size} $active={false} />
        ))}
      {Array(barCount)
        .fill(null)
        .map((_, i) => (
          <Bar key={`colored-${i}`} $color={color} $size={size} $active />
        ))}
    </BarsWrapper>
  );
};

export default BarStack;
